import './LinkRateModal.less';

import {
  Button,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useCallback, useMemo, useState } from 'react';

import { Rate } from '../../models/Rate';

interface Class {
  id: number;
  name: string;
}

interface Mode {
  id: number;
  name: string;
}

interface ShipType {
  id: number;
  name: string;
}

interface IProps {
  classes?: Class[];
  fuelSurchargePercent?: number;
  linkedRateId?: number;
  modes?: Mode[];
  onClose: () => void;
  onConfirm: (rateId: number | undefined) => void;
  rates: Rate[];
  shipTypes?: ShipType[];
}

const LinkRateModal: React.FC<IProps> = ({
  classes = [],
  fuelSurchargePercent,
  linkedRateId,
  modes = [],
  onClose,
  onConfirm,
  rates,
  shipTypes = [],
}) => {
  const [originSearch, setOriginSearch] = useState('');
  const [destinationSearch, setDestinationSearch] = useState('');
  const [modeIdFilter, setModeIdFilter] = useState<number | undefined>(
    undefined
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const closeModal = useCallback(() => {
    setOriginSearch('');
    setDestinationSearch('');
    setModeIdFilter(undefined);
    setSelectedRowKeys([]);
    setCurrentPage(1);
    onClose();
  }, [onClose]);

  const filteredRates = useMemo(() => {
    return rates.filter((rate) => {
      if (linkedRateId != null && rate.id === linkedRateId) return false;
      const originMatch =
        !originSearch.trim() ||
        rate.origin.toLowerCase().includes(originSearch.trim().toLowerCase());
      const destMatch =
        !destinationSearch.trim() ||
        rate.destination
          .toLowerCase()
          .includes(destinationSearch.trim().toLowerCase());
      const modeMatch = modeIdFilter == null || rate.modeId === modeIdFilter;
      return originMatch && destMatch && modeMatch;
    });
  }, [rates, linkedRateId, originSearch, destinationSearch, modeIdFilter]);

  const rowSelection = useMemo(
    () => ({
      onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
      selectedRowKeys,
      type: 'radio' as const,
    }),
    [selectedRowKeys]
  );

  const linkedRate = useMemo(
    () =>
      linkedRateId != null ? rates.find((r) => r.id === linkedRateId) : null,
    [rates, linkedRateId]
  );

  const baseColumns: ColumnsType<Rate> = useMemo(
    () => [
      {
        dataIndex: 'origin',
        key: 'origin',
        title: 'Origin',
        width: 180,
      },
      {
        key: 'originType',
        render: (_: unknown, record: Rate) =>
          record.originShipTypeName ??
          shipTypes.find(
            (s) =>
              s.id === record.originShipTypeId || s.id === record.shipTypeId
          )?.name ??
          '-',
        title: 'Origin Type',
        width: 135,
      },
      {
        dataIndex: 'destination',
        key: 'destination',
        title: 'Destination',
        width: 180,
      },
      {
        key: 'destType',
        render: (_: unknown, record: Rate) =>
          record.destinationShipTypeName ??
          shipTypes.find((s) => s.id === record.destinationShipTypeId)?.name ??
          '-',
        title: 'Dest Type',
        width: 135,
      },
      {
        dataIndex: 'modeId',
        key: 'mode',
        render: (modeId: number) => {
          const mode = modes.find((m) => m.id === modeId);
          return mode?.name ?? modeId;
        },
        title: 'Mode',
        width: 130,
      },
      ...classes.map((cls) => ({
        dataIndex: ['classRate', cls.id, 'value'] as const,
        key: `class-${cls.id}`,
        render: (_: unknown, record: Rate) => {
          const classRate = record.classRate?.[cls.id];
          const basePrice = classRate?.value;
          if (basePrice == null) return '-';
          const surcharge = fuelSurchargePercent ?? record.fuelSurcharge ?? 0;
          const displayValue =
            surcharge && !record.skipFuelSurcharge
              ? basePrice * ((100 + surcharge) / 100)
              : basePrice;
          return (
            <Tooltip title={`$${Number(basePrice).toFixed(2)}`}>
              <span>${Number(displayValue).toFixed(2)}</span>
            </Tooltip>
          );
        },
        title: (
          <Tooltip title={cls.name}>
            <span className="link-rate-modal__truncate">{cls.name}</span>
          </Tooltip>
        ),
        width: 80,
      })),
    ],
    [classes, fuelSurchargePercent, modes, shipTypes]
  );

  const linkedRateColumns: ColumnsType<Rate> = useMemo(
    () => [...baseColumns],
    [baseColumns]
  );

  return (
    <Modal
      centered
      footer={null}
      maskClosable={false}
      open={true}
      title="Select a Parent Rate"
      width={Math.min(1200, 748 + classes.length * 80)}
      onCancel={closeModal}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Typography.Text
            style={{ display: 'block', marginBottom: 4 }}
            type="secondary"
          >
            Origin
          </Typography.Text>
          <Input
            allowClear
            placeholder="Search origin..."
            value={originSearch}
            onChange={(e) => {
              setOriginSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col span={6}>
          <Typography.Text
            style={{ display: 'block', marginBottom: 4 }}
            type="secondary"
          >
            Destination
          </Typography.Text>
          <Input
            allowClear
            placeholder="Search destination..."
            value={destinationSearch}
            onChange={(e) => {
              setDestinationSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col span={6}>
          <Typography.Text
            style={{ display: 'block', marginBottom: 4 }}
            type="secondary"
          >
            Mode
          </Typography.Text>
          <Select
            allowClear
            placeholder="Filter by mode"
            style={{ width: '100%' }}
            value={modeIdFilter ?? null}
            onChange={(v) => {
              setModeIdFilter(v);
              setCurrentPage(1);
            }}
          >
            {modes.map((mode) => (
              <Select.Option key={mode.id} value={mode.id}>
                {mode.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <div style={{ minHeight: 340 }}>
        <Table
          columns={baseColumns}
          dataSource={filteredRates}
          locale={{ emptyText: 'No matching rates found' }}
          pagination={{
            current: currentPage,
            onChange: setCurrentPage,
            pageSize: 5,
            showSizeChanger: false,
            showTotal: (total) => `${total} rate${total !== 1 ? 's' : ''}`,
          }}
          rowKey="id"
          rowSelection={rowSelection}
          scroll={{ x: 'max-content' }}
          size="middle"
          onRow={(record) => ({
            onClick: () => setSelectedRowKeys([record.id]),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      {linkedRate != null && (
        <div className="link-rate-modal__linked-section">
          <Typography.Text
            strong
            style={{ color: '#0050b3', display: 'block', marginBottom: 8 }}
          >
            Current Parent
          </Typography.Text>
          <Table
            columns={linkedRateColumns}
            dataSource={[linkedRate]}
            pagination={false}
            rowKey="id"
            size="small"
          />
        </div>
      )}

      <Row align="middle" justify="space-between" style={{ marginTop: 16 }}>
        <Typography.Text style={{ fontSize: 12 }} type="secondary">
          All prices shown include fuel surcharge
          {fuelSurchargePercent != null
            ? ` (${fuelSurchargePercent}%)`
            : ''}{' '}
          when applicable.
        </Typography.Text>
        <Space>
          {linkedRateId != null ? (
            <Button onClick={() => onConfirm(undefined)}>
              Unlink Current Parent
            </Button>
          ) : null}
          <Button
            className="modal-close-btn"
            style={{ marginRight: 8 }}
            onClick={closeModal}
          >
            Cancel
          </Button>
          <Button
            disabled={selectedRowKeys.length === 0}
            type="primary"
            onClick={() => {
              onConfirm(selectedRowKeys[0] as number | undefined);
              closeModal();
            }}
          >
            {linkedRateId != null ? 'Update Parent' : 'Set as Parent'}
          </Button>
        </Space>
      </Row>
    </Modal>
  );
};

export default LinkRateModal;
